import { useState } from 'react';

import { useSnackbar } from 'notistack';

import { normalizeImageFileForUpload } from '~/components/Common/Images/cropUtils';

import { MAX_SVC_IMAGE_UPLOAD_BYTES, getUploadedImageId, uploadImage } from '~/services/ImageService';
import OffersService from '~/services/OffersService';
import ReservationService from '~/services/ReservationService';

import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

import type { MediaImage, PropertyRoomTypeWithImages } from './ImagesVideo/types';
import {
  allSettledWithConcurrency,
  buildUploadPlans,
  dedupeMediaImages,
  findPersistedRoomByKey,
  isAllowedImageFile,
} from './ImagesVideo/utils';
import type { UploadRoomAssignment, UploadedRoomAssignment } from './ImagesVideo/utils';

const IMAGE_UPLOAD_CONCURRENCY = 5;

export interface UploadFilesOptions {
  ignoreNonImageFiles?: boolean;
  roomAssignment?: UploadRoomAssignment;
  skipRoomMatching?: boolean;
}

type PendingOversizedUpload = {
  file: File;
  options: UploadFilesOptions;
};

export type UploadFilesResult = 'uploaded' | 'queued' | 'failed';

interface UseOfferBuilderMediaUploadParams {
  offerId: string;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
}

export interface OfferBuilderMediaUploadController {
  uploadedImages: Array<MediaImage>;
  setUploadedImages: (updater: (currentImages: Array<MediaImage>) => Array<MediaImage>) => void;
  uploadedRoomAssignments: Array<UploadedRoomAssignment>;
  isUploadingImages: boolean;
  pendingOversizedUpload?: { file: File; filename: string };
  removeUploadedImage: (publicId: string) => void;
  replaceUploadedImage: (publicId: string, nextImage: MediaImage) => void;
  uploadFiles: (files: Array<File>, options?: UploadFilesOptions) => Promise<UploadFilesResult>;
  uploadFilesToOfferGallery: (files: Array<File>) => Promise<void>;
  uploadFilesToRoom: (roomId: string, roomName: string, files: Array<File>) => Promise<void>;
  cancelOversizedUpload: () => void;
  confirmOversizedUpload: (file: File) => Promise<boolean>;
}

export default function useOfferBuilderMediaUpload({
  offerId,
  propertyRoomTypes,
}: UseOfferBuilderMediaUploadParams): OfferBuilderMediaUploadController {
  const { enqueueSnackbar } = useSnackbar();
  const [uploadedImages, setUploadedImages] = useState<Array<MediaImage>>([]);
  const [uploadedRoomAssignments, setUploadedRoomAssignments] = useState<Array<UploadedRoomAssignment>>([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [pendingOversizedUploads, setPendingOversizedUploads] = useState<Array<PendingOversizedUpload>>([]);

  const removeUploadedImage = (publicId: string) => {
    setUploadedImages((currentImages) => currentImages.filter((currentImage) => currentImage.publicId !== publicId));
  };

  const replaceUploadedImage = (publicId: string, nextImage: MediaImage) => {
    setUploadedImages((currentImages) => dedupeMediaImages(currentImages.concat(nextImage)));
  };

  const uploadFiles = async (files: Array<File>, options: UploadFilesOptions = {}) => {
    const candidateFiles = options.ignoreNonImageFiles ? files.filter(isAllowedImageFile) : files;
    const uploadableFiles = candidateFiles.filter(isAllowedImageFile);
    const rejectedFileCount = candidateFiles.length - uploadableFiles.length;

    if (rejectedFileCount > 0) {
      enqueueSnackbar(
        `${rejectedFileCount} ${rejectedFileCount === 1 ? 'file was' : 'files were'} skipped. Use supported images.`,
        { variant: 'warning' },
      );
    }

    if (uploadableFiles.length === 0) {
      return 'failed';
    }

    const oversizedFiles = uploadableFiles.filter((file) => file.size > MAX_SVC_IMAGE_UPLOAD_BYTES);
    const regularFiles = uploadableFiles.filter((file) => file.size <= MAX_SVC_IMAGE_UPLOAD_BYTES);
    const normalizedOversizedFiles: Array<File> = [];
    const normalizedOversizedSourceFiles: Array<File> = [];
    const stillOversizedFiles: Array<File> = [];

    for (const file of oversizedFiles) {
      try {
        const normalizedFile = await normalizeImageFileForUpload({
          file,
          maxBytes: MAX_SVC_IMAGE_UPLOAD_BYTES,
        });

        if (normalizedFile.size <= MAX_SVC_IMAGE_UPLOAD_BYTES) {
          normalizedOversizedFiles.push(normalizedFile);
          normalizedOversizedSourceFiles.push(file);
        } else {
          stillOversizedFiles.push(file);
        }
      } catch {
        stillOversizedFiles.push(file);
      }
    }

    if (stillOversizedFiles.length > 0) {
      setPendingOversizedUploads((currentUploads) =>
        currentUploads.concat(stillOversizedFiles.map((file) => ({ file, options }))),
      );
    }

    const filesToUpload = regularFiles.concat(normalizedOversizedFiles);
    const sourceFilesForUpload = regularFiles.concat(normalizedOversizedSourceFiles);

    if (filesToUpload.length === 0) {
      return stillOversizedFiles.length > 0 ? 'queued' : 'failed';
    }

    setIsUploadingImages(true);

    try {
      const uploadPlans = buildUploadPlans({
        files: filesToUpload,
        propertyRoomTypes: options.skipRoomMatching ? undefined : propertyRoomTypes,
        roomAssignment: options.roomAssignment,
      }).map((uploadPlan, index) => ({ ...uploadPlan, uploadIndex: index }));

      const uploadResults = await allSettledWithConcurrency(
        uploadPlans,
        IMAGE_UPLOAD_CONCURRENCY,
        async ({
          file,
          roomAssignment,
          uploadIndex,
        }): Promise<{
          image: MediaImage;
          roomAssignment?: UploadedRoomAssignment;
        }> => {
          const normalizedFile = await normalizeImageFileForUpload({
            file,
            maxBytes: MAX_SVC_IMAGE_UPLOAD_BYTES,
          });
          const response = await uploadImage(normalizedFile);
          const uploadedImageId = getUploadedImageId(response);

          if (!uploadedImageId) {
            throw new Error('Image service did not return an image id');
          }

          let persistedOfferImage;
          let persistedRoomImage: Awaited<ReturnType<typeof createPersistedRoomImage>> | undefined;

          if (roomAssignment) {
            try {
              persistedRoomImage = await createPersistedRoomImage({
                propertyRoomTypes,
                publicId: uploadedImageId,
                roomAssignment,
                uploadIndex,
              });
            } catch (error) {
              if (error instanceof Error) {
                reportError(error);
              }
            }
          } else {
            persistedOfferImage = await OffersService.createImage(
              {
                filename: normalizedFile.name,
                id_cloudinary_external: uploadedImageId,
              },
              offerId,
            );
          }

          return {
            image: {
              hidden: persistedRoomImage?.hidden ?? persistedOfferImage?.hidden,
              order: persistedRoomImage?.order ?? persistedOfferImage?.order,
              publicId: persistedOfferImage?.id_cloudinary_external ?? uploadedImageId,
              sourceLabel: roomAssignment?.roomName ?? 'Offer gallery',
              sourceImageId:
                persistedRoomImage?.id ??
                (persistedOfferImage ? String(persistedOfferImage.id ?? persistedOfferImage.id_image) : undefined),
              sourcePropertyId: persistedRoomImage?.sourcePropertyId,
              sourceRoomId: roomAssignment?.roomId,
              sourceRoomTypeId: persistedRoomImage?.sourceRoomTypeId,
              sourceType: roomAssignment ? 'room' : 'offer',
              title: persistedRoomImage?.title || persistedOfferImage?.title || undefined,
            },
            roomAssignment: roomAssignment
              ? {
                  ...roomAssignment,
                  publicId: uploadedImageId,
                }
              : undefined,
          };
        },
      );
      const uploadedMediaImages: Array<MediaImage> = [];
      const newUploadedRoomAssignments: Array<UploadedRoomAssignment> = [];
      const failedOversizedFiles: Array<File> = [];

      uploadResults.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          uploadedMediaImages.push(result.value.image);
          if (result.value.roomAssignment) {
            newUploadedRoomAssignments.push(result.value.roomAssignment);
          }
          return;
        }

        if (result.reason instanceof Error) {
          reportError(result.reason);
        }

        const sourceFile = sourceFilesForUpload[index];
        if (sourceFile?.size > MAX_SVC_IMAGE_UPLOAD_BYTES) {
          failedOversizedFiles.push(sourceFile);
        }
      });

      if (failedOversizedFiles.length > 0) {
        setPendingOversizedUploads((currentUploads) =>
          currentUploads.concat(failedOversizedFiles.map((file) => ({ file, options }))),
        );
      }

      const failedUploadCount = uploadResults.length - uploadedMediaImages.length - failedOversizedFiles.length;

      if (uploadedMediaImages.length > 0) {
        setUploadedImages((currentImages) => dedupeMediaImages(currentImages.concat(uploadedMediaImages)));
        setUploadedRoomAssignments((currentAssignments) => currentAssignments.concat(newUploadedRoomAssignments));
        enqueueSnackbar(
          `${uploadedMediaImages.length} ${uploadedMediaImages.length === 1 ? 'image' : 'images'} uploaded`,
          { variant: 'success' },
        );
      }

      if (failedUploadCount > 0) {
        enqueueSnackbar(`${failedUploadCount} ${failedUploadCount === 1 ? 'image' : 'images'} failed to upload`, {
          variant: 'error',
        });
      }

      return failedUploadCount === 0 ? (failedOversizedFiles.length > 0 ? 'queued' : 'uploaded') : 'failed';
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'Failed to upload images'), { variant: 'error' });
      if (error instanceof Error) {
        reportError(error);
      }

      return 'failed';
    } finally {
      setIsUploadingImages(false);
    }
  };

  const uploadFilesToOfferGallery = async (files: Array<File>) => {
    await uploadFiles(files, { skipRoomMatching: true });
  };

  const uploadFilesToRoom = async (roomId: string, roomName: string, files: Array<File>) => {
    await uploadFiles(files, { roomAssignment: { roomId, roomName } });
  };

  const cancelOversizedUpload = () => {
    setPendingOversizedUploads((currentUploads) => currentUploads.slice(1));
  };

  const confirmOversizedUpload = async (file: File) => {
    const pendingUpload = pendingOversizedUploads[0];

    if (!pendingUpload) {
      return false;
    }

    const uploaded = await uploadFiles([file], pendingUpload.options);
    return uploaded === 'uploaded';
  };

  return {
    uploadedImages,
    setUploadedImages,
    uploadedRoomAssignments,
    isUploadingImages,
    pendingOversizedUpload: pendingOversizedUploads[0]
      ? { file: pendingOversizedUploads[0].file, filename: pendingOversizedUploads[0].file.name }
      : undefined,
    removeUploadedImage,
    replaceUploadedImage,
    uploadFiles,
    uploadFilesToOfferGallery,
    uploadFilesToRoom,
    cancelOversizedUpload,
    confirmOversizedUpload,
  };
}

async function createPersistedRoomImage({
  propertyRoomTypes,
  publicId,
  roomAssignment,
  uploadIndex,
}: {
  propertyRoomTypes: Array<PropertyRoomTypeWithImages> | undefined;
  publicId: string;
  roomAssignment: UploadRoomAssignment;
  uploadIndex: number;
}) {
  const room = findPersistedRoomByKey(propertyRoomTypes, roomAssignment.roomId);
  const propertyId = room?.property_id;
  const roomTypeId = room?.id;

  if (!propertyId || !roomTypeId) {
    return undefined;
  }

  const roomImage = await ReservationService.createRoomTypeImage(
    {
      hidden: false,
      id_cloudinary_external: publicId,
      order: (room.images?.length ?? 0) + uploadIndex + 1,
    },
    propertyId,
    roomTypeId,
  );

  return { ...roomImage, sourcePropertyId: propertyId, sourceRoomTypeId: roomTypeId };
}
